<template>
    <div v-if="isVisible" class="LinkedInSynchronizeSwitch flex shrink-0">
        <CFormCheckbox :disabled="disabled" slim :model-value="isChecked" variant="switch" @update:model-value="updateChecked">
            <span class="font-medium">Sync</span>

            <CTooltip variant="light">
                <span
                    class="[&_em]:rounded-sm [&_em]:bg-secondary-500 [&_em]:p-0.5 [&_i]:text-xs [&_i]:italic"
                    v-html="tt(`linkedInFields.${field}.${isChecked ? 'on' : 'off'}`)"
                />
            </CTooltip>
        </CFormCheckbox>
    </div>

    <div v-else-if="domains.length" class="LinkedInSynchronizeSwitch flex">
        <CTooltip variant="light">
            <span class="[&_em]:rounded-sm [&_em]:bg-secondary-500 [&_em]:p-0.5 [&_i]:text-xs [&_i]:italic">
                Pre zobrazenie a synchronizáciu týchto údajov z LinkedIn je potrebné udeliť jeden z nasledujúcich
                súhlasov:
                <span v-for="(domain, idx) in domains" :key="domain">
                    <template v-if="idx">, </template>
                    <em>{{ tt(`linkedInDomains.${domain}.title`) }}</em>
                </span>
                <br /><br />
                <i v-if="!disabled">
                    Môžete tak učiniť po kliknutí na tlačidlo "Upraviť" v sekcií LinkedIn prepojenie (na vrchu stránky).
                </i>
            </span>
        </CTooltip>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import cloneDeep from 'lodash-es/cloneDeep'

import { useLocalTranslation } from '@/composables/useLocalTranslation'
import { LINKED_IN_FIELD_DOMAINS, LINKEDIN_DOMAIN_FIELDS } from '#shared/constants/linkedIn'
import { EMemberSnapshotDataDomains } from '#shared/types/LinkedIn'
import type { UserPrivateProfile, UserProfile } from '#shared/types/User'
import {
    isFieldFetched,
    isFieldSynchronized,
    isSynchronizationAvailable,
    switchFieldSynchronization,
    switchFieldValue
} from '#shared/utils/linkedIn'

type TFields = typeof LINKEDIN_DOMAIN_FIELDS
type TValue = UserProfile[keyof UserProfile] | UserPrivateProfile[keyof UserPrivateProfile]

const props = defineProps({
    lang: {
        type: String as PropType<'sk' | 'en'>,
        default: 'sk'
    },
    value: {
        type: [String, Number, Boolean, Object, Array, Symbol] as PropType<TValue>,
        default: undefined
    },
    field: {
        type: String as PropType<TFields[keyof TFields][number]>,
        required: true
    },
    connectionData: {
        type: Object as PropType<any | null>,
        default: null
    },
    profileData: {
        type: Object as PropType<any | null>,
        default: null
    },
    disabled: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits(['update:connection-data', 'input'])

const lang = computed(() => props.lang)
const { tt } = useLocalTranslation(lang)

const oldValue = ref(cloneDeep(props.value))
const firstLoad = ref(true)

const domains = computed((): EMemberSnapshotDataDomains[] => {
    if (!props.connectionData) return []
    return LINKED_IN_FIELD_DOMAINS[props.field] || []
})

const isVisible = computed((): boolean => {
    return isSynchronizationAvailable(props.field, props.connectionData?.domains)
})

const isChecked = computed((): boolean => {
    return isFieldSynchronized(props.field, props.connectionData?.syncFields)
})

watch(
    () => [props.connectionData, props.profileData],
    ([connection, profile]: [any | null, any | null]) => {
        if (!profile || !connection) return
        if (!firstLoad.value) return

        const isSync = isFieldSynchronized(props.field, connection?.syncFields)
        if (!isSync) return

        const isFetched = isFieldFetched(props.field, connection?.domains, profile?.domains)
        if (!isFetched) {
            const newFields = switchFieldSynchronization(false, props.field, props.connectionData?.syncFields)

            emit('update:connection-data', {
                ...props.connectionData,
                syncFields: newFields
            })
        }

        // profile is already { public: {...}, private: {...} }, no need for .profile
        const newValue = switchFieldValue(isFetched, props.field, oldValue.value, profile)
        emit('input', newValue)

        firstLoad.value = false
    },
    { immediate: true, deep: true }
)

watch(isVisible, (value: boolean) => {
    if (value || oldValue.value === props.value) return

    // profileData is already { public: {...}, private: {...} }, no need for .profile
    const newValue = switchFieldValue(false, props.field, oldValue.value, props.profileData || null)
    emit('input', newValue)
})

function updateOldValue() {
    oldValue.value = cloneDeep(props.value)
}

function updateChecked(value: boolean) {
    if (value) updateOldValue()

    const newFields = switchFieldSynchronization(value, props.field, props.connectionData?.syncFields)

    emit('update:connection-data', {
        ...props.connectionData,
        syncFields: newFields
    })

    // profileData is already { public: {...}, private: {...} }, no need for .profile
    const newValue = switchFieldValue(value, props.field, oldValue.value, props.profileData || null)

    emit('input', newValue)
}
</script>